import classNames from 'classnames';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import PopoverPlacement from '@ui5/webcomponents/dist/types/PopoverPlacement';

import styles from './TooltipIcon.module.css';
import { Icon } from '../Icon/Icon';
import { Tooltip } from '../Tooltip';

export interface TooltipIconProps {
  /**
   * Aria label for the icon.
   * Defaults to "Show more information"
   */
  'aria-label'?: string;
  /**
   * Optional additional class name(s) to add to the element,
   * for adjusting positioning of the element. Do not alter how the component looks!
   */
  className?: string;
  /**
   * Placement of the tooltip relative to the icon.
   * Defaults to Top.
   */
  placement?: PopoverPlacement;
  /**
   * Optional CSS inline styles to add to the element,
   * for adjusting positioning of the element. Do not alter how the component looks!
   */
  style?: CSSProperties;
  /**
   * The contents of the tooltip to show when open.
   * The tooltip contents should be a single paragraph of text, and therefore
   * should not contain other markup than plain text.
   */
  tooltipText: string;
}

/**
 * Tooltip icon shows a tooltip when clicked.
 * The tooltip hides when clicking the icon again or clicking anywhere outside.
 *
 * Tooltip only show a short paragraph of text and it should never contain
 * interactive content. Therefore, only text is accepted as the tooltip contents.
 *
 * This component takes care of the accessibility. It uses `Tooltip` component internally.
 */
export function TooltipIcon({
  'aria-label': ariaLabel = 'Show more information',
  className,
  style,
  tooltipText,
  placement = PopoverPlacement.Top,
}: TooltipIconProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Use more explicit control for button click
  const handleButtonClick = useCallback(() => {
    // If tooltip is already visible, just close it
    if (isTooltipVisible) {
      setIsTooltipVisible(false);
    } else {
      // Otherwise open it
      setIsTooltipVisible(true);
    }
  }, [isTooltipVisible]);

  // Set up global event listeners for mouse and keyboard events
  useEffect(() => {
    if (!isTooltipVisible) {
      return;
    }

    const handleOutsideInteraction = (e: MouseEvent) => {
      // If the click was on the button, let the button handler deal with it
      if (buttonRef.current && buttonRef.current.contains(e.target as Node)) {
        return;
      }

      // Otherwise close the tooltip
      setIsTooltipVisible(false);
    };

    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideInteraction);

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);

      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isTooltipVisible]);

  return (
    <Tooltip
      placement={placement}
      tooltipText={tooltipText}
      visible={isTooltipVisible}
    >
      <button
        aria-label={ariaLabel}
        className={classNames(className, styles.button)}
        onClick={handleButtonClick}
        ref={buttonRef}
        style={style}
        type="button"
      >
        <Icon name="message-information" />
      </button>
    </Tooltip>
  );
}
